import React, { useState } from "react";
import {
  Settings, Clock, Sparkles, ShieldCheck, Download,
  Upload, CheckCircle2, Key, Globe, Database, Save, AlertCircle
} from "lucide-react";
import { SeoMarket, PusherSettings, ContentItem, BacklinkItem, KeywordItem, CompetitorItem, SeoSwitches } from "./types";
import { MODE_LABEL } from "./engineFormat";

// Where each integration's credentials live (AWS Secrets Manager) and the switch that turns it on.
const INTEGRATIONS: Array<{ key: keyof SeoSwitches; name: string; secret: string; what: string }> = [
  { key: "dataforseo", name: "DataForSEO", secret: "prod/seo/dataforseo", what: "SERP-placeringer, søgevolumen, sværhedsgrad, konkurrent-backlinks" },
  { key: "llm", name: "Claude på AWS Bedrock", secret: "Lambda-rollen (bedrock-mantle:CreateInference)", what: "Nyhedsscoring og artikeludkast" },
  { key: "indexing", name: "IndexNow + Search Console", secret: "prod/seo/indexnow · campaign_fetcher_google_credentials", what: "Ping af nye URL'er, sitemap-genindsendelse, URL Inspection" },
  { key: "radar", name: "Konkurrent-radar", secret: "–", what: "Sitemaps og sammenligningssider hos peer-affiliates" },
  { key: "publish", name: "Udgivelse", secret: "prod/seo/deploy-hook (valgfri build-trigger)", what: "Artikler går i site-eksporten og bygges live" },
];

interface SeoSettingsTabProps {
  market: SeoMarket;
  settings: PusherSettings;
  setSettings: React.Dispatch<React.SetStateAction<PusherSettings>>;
  contentList: ContentItem[];
  setContentList: React.Dispatch<React.SetStateAction<ContentItem[]>>;
  backlinksList: BacklinkItem[];
  setBacklinksList: React.Dispatch<React.SetStateAction<BacklinkItem[]>>;
  keywordsList: KeywordItem[];
  setKeywordsList: React.Dispatch<React.SetStateAction<KeywordItem[]>>;
  competitorsList: CompetitorItem[];
  setCompetitorsList: React.Dispatch<React.SetStateAction<CompetitorItem[]>>;
  // Set when the SEO engine is connected: settings are saved server-side and replan the calendar.
  engine?: { save: (s: PusherSettings) => Promise<void>; switches: SeoSwitches | null };
}

export default function SeoSettingsTab({
  market,
  settings,
  setSettings,
  contentList,
  setContentList,
  backlinksList,
  setBacklinksList,
  keywordsList,
  setKeywordsList,
  competitorsList,
  setCompetitorsList,
  engine
}: SeoSettingsTabProps) {
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSaveSettings = async () => {
    setSaveError(null);
    if (engine) {
      setSaving(true);
      try {
        await engine.save(settings);
      } catch (e) {
        setSaveError((e as { message?: string })?.message || "Kunne ikke gemme");
        return;
      } finally {
        setSaving(false);
      }
    }
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 3000);
  };

  // Export all data to JSON file
  const handleExportBackup = () => {
    const backupData = {
      exportedAt: new Date().toISOString(),
      market,
      contentList,
      backlinksList,
      keywordsList,
      competitorsList,
      settings
    };

    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `simplyloan-seo-backup-${market.toLowerCase()}-${new Date().toISOString().split("T")[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Import data from JSON file
  const handleImportBackup = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (parsed.contentList) setContentList(parsed.contentList);
        if (parsed.backlinksList) setBacklinksList(parsed.backlinksList);
        if (parsed.keywordsList) setKeywordsList(parsed.keywordsList);
        if (parsed.competitorsList) setCompetitorsList(parsed.competitorsList);
        if (parsed.settings) setSettings(parsed.settings);
        alert("Sikkerhedskopi blev indlæst med succes!");
      } catch (err) {
        alert("Kunne ikke indlæse filen. Kontrollér at det er en gyldig SimplyLoan backup JSON fil.");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="space-y-6">

      {savedSuccess && (
        <div className="p-4 rounded-xl bg-emerald-100 border border-emerald-300 text-emerald-900 text-sm font-medium flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <span>Indstillingerne er gemt!</span>
        </div>
      )}

      {/* Anti-Footprint Pusher Settings */}
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#0F3F34]" />
            <span>Udgivelsesrytme (Pusher)</span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            En jævn, menneskelig rytme fra en godkendt buffer: faste dage, et tidsvindue og tilfældig variation omkring
            to faste tidspunkter. Google rangerer på kvalitet, ikke på tidsstempler – rytmen forhindrer bulk-udgivelser,
            og kvalitetstjekket og den menneskelige review er det, der beskytter sitet.
          </p>
        </div>

        <div className="space-y-5 text-sm text-gray-700">

          {engine && (
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <input type="checkbox" checked={settings.enabled}
                     onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))} />
              <span>Pusher aktiv for {market}</span>
              {engine.switches && (
                <span className="text-xs font-normal text-gray-500">
                  (udgivelse: {MODE_LABEL[engine.switches.publish] || engine.switches.publish})
                </span>
              )}
            </label>
          )}
          
          {/* Active Days */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
              Ugentlige Udgivelsesdage
            </label>
            <div className="flex flex-wrap gap-2">
              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => {
                const isSelected = settings.activeDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setSettings(prev => ({
                        ...prev,
                        activeDays: isSelected
                          ? prev.activeDays.filter(d => d !== day)
                          : [...prev.activeDays, day]
                      }));
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                      isSelected
                        ? "bg-[#0F3F34] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Window & Jitter */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                Tidligste vindue
              </label>
              <input
                type="time"
                value={settings.windowStart}
                onChange={(e) => setSettings(prev => ({ ...prev, windowStart: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                Seneste vindue
              </label>
              <input
                type="time"
                value={settings.windowEnd}
                onChange={(e) => setSettings(prev => ({ ...prev, windowEnd: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>

            {engine ? (
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Jitter ± min–maks (minutter)
                </label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="180" value={settings.jitterMinMinutes ?? 30}
                         onChange={(e) => setSettings(prev => ({ ...prev, jitterMinMinutes: Number(e.target.value) || 0 }))}
                         className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" />
                  <span className="text-gray-400">–</span>
                  <input type="number" min="0" max="240" value={settings.jitterMaxMinutes ?? 75}
                         onChange={(e) => setSettings(prev => ({ ...prev, jitterMaxMinutes: Number(e.target.value) || 0 }))}
                         className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Jitter Variation (Minutter)
                </label>
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={settings.jitterMinutes}
                  onChange={(e) => setSettings(prev => ({ ...prev, jitterMinutes: Number(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                />
              </div>
            )}
          </div>

          {engine && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Indlæg pr. udgivelsesdag</label>
                <input type="number" min="1" max="4" value={settings.postsPerActiveDay ?? 1}
                       onChange={(e) => setSettings(prev => ({ ...prev, postsPerActiveDay: Number(e.target.value) || 1 }))}
                       className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" />
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-700 pt-5">
                <input type="checkbox" checked={settings.skipHolidays ?? true}
                       onChange={(e) => setSettings(prev => ({ ...prev, skipHolidays: e.target.checked }))} />
                <span>Spring helligdage og aftener over (jul, midsommer, 17. mai …)</span>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 pt-5">
                <input type="checkbox" checked={settings.newsFastLane ?? true}
                       onChange={(e) => setSettings(prev => ({ ...prev, newsFastLane: e.target.checked }))} />
                <span>Hurtigspor for godkendte nyheder (samme eller næste hverdag)</span>
              </label>
            </div>
          )}

          {/* Buffer Alert Threshold */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
              Minimums-buffer Advarselsgrænse (Dage)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="3"
                max="30"
                value={settings.targetBufferMinDays}
                onChange={(e) => setSettings(prev => ({ ...prev, targetBufferMinDays: Number(e.target.value) || 7 }))}
                className="w-24 px-3 py-2 border border-gray-200 rounded-xl text-sm font-bold"
              />
              <span className="text-xs text-gray-500">
                Hvis antallet af artikler i bufferen dækker færre end {settings.targetBufferMinDays} dages udgivelser, vises en advarsel i Daily Reports.
              </span>
            </div>
          </div>

          <div className="pt-2 flex items-center gap-3">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="px-4 py-2.5 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold text-xs rounded-xl transition shadow-xs flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{engine ? "Gem og planlæg kalenderen igen" : "Gem Pusher-indstillinger"}</span>
            </button>
            {saveError && <span className="text-xs text-red-700">{saveError}</span>}
          </div>

        </div>
      </div>

      {/* Paid API Integration Slots (Prepared for when you decide to invest) */}
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-900">
              Integrationer
            </h3>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Nøgler indtastes aldrig i browseren: de ligger i AWS Secrets Manager, og hver integration tændes med en
            miljøvariabel på Lambdaen (SEO_*_MODE). Alle står som standard på den sikre værdi.
          </p>
        </div>

        <div className="divide-y divide-gray-100 text-xs">
          {INTEGRATIONS.map((it) => {
            const mode = engine?.switches?.[it.key];
            return (
              <div key={it.key} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="font-bold text-gray-800">{it.name}</div>
                  <div className="text-gray-500">{it.what}</div>
                  <div className="text-gray-400 font-mono text-[11px]">{it.secret}</div>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${
                  mode === "live" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : mode ? "border-amber-200 bg-amber-50 text-amber-800" : "border-gray-200 bg-gray-50 text-gray-500"}`}>
                  {mode ? MODE_LABEL[mode] || mode : "Backend ikke forbundet"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Backup, Import & Data Safety */}
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-[#0F3F34]" />
            <span>Data Sikkerhedskopi & Eksport</span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Eksportér eller gendan alle dine artikler, backlinks, overvågede søgeord og konkurrent-noter til en JSON-fil.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            onClick={handleExportBackup}
            className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition flex items-center gap-2 shadow-xs"
          >
            <Download className="w-4 h-4" />
            <span>Download komplet JSON backup ({market})</span>
          </button>

          <label className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition flex items-center gap-2 cursor-pointer">
            <Upload className="w-4 h-4 text-gray-500" />
            <span>Gendan fra JSON backup</span>
            <input
              type="file"
              accept=".json"
              onChange={handleImportBackup}
              className="hidden"
            />
          </label>
        </div>
      </div>

    </div>
  );
}
